import posthog from 'posthog-js';

export interface ErrorContext {
  component?: string;
  action?: string;
  userId?: string;
  userRole?: string;
  url?: string;
  timestamp?: string;
  additionalData?: Record<string, any>;
}

interface QueuedError {
  errorMessage: string;
  errorType: string;
  context: ErrorContext;
  timestamp: string;
}

// Error queue for when PostHog is not loaded
const errorQueue: QueuedError[] = [];
const MAX_QUEUE_SIZE = 50; // Limit queue size to prevent memory issues

// Flush queued errors when PostHog loads
export const flushErrorQueue = () => {
  if (!posthog.__loaded) {
    console.warn('Cannot flush error queue: PostHog not loaded');
    return;
  }
  
  console.log(`Flushing ${errorQueue.length} queued errors to PostHog`);
  
  while (errorQueue.length > 0) {
    const queuedError = errorQueue.shift();
    if (queuedError) {
      posthog.capture('$exception', {
        $exception_message: queuedError.errorMessage,
        $exception_type: queuedError.errorType,
        $exception_stack: new Error(queuedError.errorMessage).stack,
        ...queuedError.context,
        timestamp: queuedError.timestamp,
        queued: true, // Flag to indicate this was queued
      });
    }
  }
};

export const trackError = (
  errorMessage: string,
  errorType: string = 'ApplicationError',
  context: ErrorContext = {}
) => {
  console.log('Tracking error:', { errorMessage, errorType, context });
  
  if (posthog.__loaded) {
    posthog.capture('$exception', {
      $exception_message: errorMessage,
      $exception_type: errorType,
      $exception_stack: new Error(errorMessage).stack,
      ...context,
      timestamp: new Date().toISOString(),
    });
    console.log('Error tracked in PostHog');
  } else {
    // Queue error for later
    if (errorQueue.length < MAX_QUEUE_SIZE) {
      errorQueue.push({
        errorMessage,
        errorType,
        context,
        timestamp: new Date().toISOString(),
      });
      console.warn('PostHog not loaded, error queued for later', errorQueue.length);
    } else {
      console.error('Error queue full, dropping error:', errorMessage);
    }
  }
};

export const trackValidationError = (
  field: string,
  message: string,
  component: string,
  additionalData: Record<string, any> = {}
) => {
  trackError(`Validation Error: ${message}`, 'ValidationError', {
    component,
    action: 'form_validation',
    field,
    validationMessage: message,
    ...additionalData,
  });
};

export const trackAuthError = (
  errorMessage: string,
  authStep: string,
  additionalData: Record<string, any> = {}
) => {
  trackError(`Auth Error: ${errorMessage}`, 'AuthenticationError', {
    component: 'Authentication',
    action: 'auth_error',
    authStep,
    ...additionalData,
  });
};

export const trackAPIError = (
  errorMessage: string,
  endpoint: string,
  statusCode?: number,
  additionalData: Record<string, any> = {}
) => {
  trackError(`API Error: ${errorMessage}`, 'APIError', {
    component: 'API',
    action: 'api_error',
    endpoint,
    statusCode,
    ...additionalData,
  });
};

export const trackUserActionError = (
  action: string,
  errorMessage: string,
  component: string,
  additionalData: Record<string, any> = {}
) => {
  trackError(`User Action Error: ${errorMessage}`, 'UserActionError', {
    component,
    action,
    ...additionalData,
  });
};

// Enhanced error tracking for React components
export const trackComponentError = (
  componentName: string,
  error: Error,
  additionalData: Record<string, any> = {}
) => {
  trackError(
    `Component Error: ${error.message}`,
    'ComponentError',
    {
      component: componentName,
      action: 'component_render',
      errorStack: error.stack,
      ...additionalData,
    }
  );
};
